import type { LearnerFeedbackAttributes } from '@/server/models'

import LearnerFeedbackFeaturedQ from '@/queries/LearnerFeedbackFeaturedQ'
import { SQL, type SQLFragment } from '@/client/query'
import type { LearnerFeedbackType } from '@/client/services/learnerFeedback/types'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOffsetToSQL,
  queryOrderToSQL,
} from './common'
import SessionQ from './SessionQ'
import UserQ from './UserQ'

const LearnerFeedbackQ = {
  ...makeQueryModelV2<LearnerFeedbackAttributes>('learner_feedback')([
    'id',
    'session_id',
    'reviewer_user_id',
    'reviewee_user_id',
    'type',
    'comment',
    'attribute_tags',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Reviewer: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: LearnerFeedbackQ.reviewer_user_id,
    },
    Reviewee: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: LearnerFeedbackQ.reviewee_user_id,
    },
    Session: {
      query: SessionQ,
      foreignKey: SessionQ.id,
      sourceKey: LearnerFeedbackQ.session_id,
    },
  }),

  where: (
    {
      id,
      sessionId,
      reviewerUserId,
      revieweeUserId,
      type,
      include = [],
      limit = null,
      offset = null,
      groupBy = null,
      order = null,
      paranoid = true,
    }: {
      id?: number | SQLFragment<number>
      sessionId?: number | null | SQLFragment<number>
      reviewerUserId?: number | null | SQLFragment<number>
      revieweeUserId?: number | SQLFragment<number>
      type?: LearnerFeedbackType | SQLFragment<string>
      include?: IncludeAssociations
      limit?: number | null
      offset?: number | null
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<LearnerFeedbackAttributes> => SQL`
        FROM ${LearnerFeedbackQ}
        ${includeAssociationsToSql(LearnerFeedbackQ, include)}
        WHERE
            ${id != null ? SQL`${LearnerFeedbackQ.id} = ${id} AND` : SQL``}
            ${sessionId != null ? SQL`${LearnerFeedbackQ.session_id} = ${sessionId} AND` : SQL``}
            ${
              reviewerUserId != null
                ? SQL`${LearnerFeedbackQ.reviewer_user_id} = ${reviewerUserId} AND`
                : SQL``
            }
            ${
              revieweeUserId != null
                ? SQL`${LearnerFeedbackQ.reviewee_user_id} = ${revieweeUserId} AND`
                : SQL``
            }
            ${type != null ? SQL`${LearnerFeedbackQ.type} = ${type} AND` : SQL``}
            ${paranoid ? SQL`${LearnerFeedbackQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
        ${queryOffsetToSQL(offset)}
    `,

  isLearnerFeedbackType: (type: LearnerFeedbackType): SQLFragment<boolean> =>
    SQL`${LearnerFeedbackQ.type} = ${type}`,

  selfReflectionFilter: (isSelfReflection?: boolean): SQLFragment<boolean> =>
    isSelfReflection == null
      ? SQL`TRUE`
      : isSelfReflection
        ? SQL`${LearnerFeedbackQ.reviewer_user_id} IS NOT DISTINCT FROM ${LearnerFeedbackQ.reviewee_user_id}`
        : SQL`${LearnerFeedbackQ.reviewer_user_id} IS DISTINCT FROM ${LearnerFeedbackQ.reviewee_user_id}`,

  isFeatured: (userId: number | SQLFragment<number>): SQLFragment<boolean> => SQL`
    EXISTS(
      SELECT 1 ${LearnerFeedbackFeaturedQ.where({
        learnerFeedbackId: LearnerFeedbackQ.id,
        userId,
      })}
    )
  `,

  isSelfReflection: (): SQLFragment<boolean> =>
    SQL`${LearnerFeedbackQ.reviewer_user_id} IS NOT DISTINCT FROM ${LearnerFeedbackQ.reviewee_user_id}`,
}

export default LearnerFeedbackQ
